import { clipToRange } from '@/utils';

type ClippedEntry = {
    startMs: number | undefined;
    endMs: number | undefined;
    timestampKey: string;
    clipped: readonly unknown[];
};

/**
 * How many sets of bounds one series holds a clipped answer for.
 *
 * One series can be the answer to more than one range at a time: two hooks whose ranges land inside the
 * same hour are handed the very same window out of the query cache while still asking for different
 * bounds. A single entry would leave the two of them overwriting each other's answer on every render, so
 * this is what lets both keep a stable one. It is well above the ranges one page shows at once.
 */
const CLIPPED_BOUNDS_PER_SERIES = 4;

/**
 * Clips a series to a set of bounds and hands back the array clipped last while nothing has changed.
 *
 * Entries are weakly keyed on the series array's own object identity, so an answer goes when react-query
 * drops the rows behind it. Each series holds one entry per set of bounds it is read under, up to
 * {@link CLIPPED_BOUNDS_PER_SERIES}, and reading an entry moves it to the end, so the bounds evicted are
 * the ones least recently asked for.
 *
 * The identity of what {@link clip} returns is what the draw effects downstream compare on. Clipping runs
 * on every combine, and the range being clipped to moves as often as twelve times an hour for a caller
 * whose bounds track the present, so holding the last answer is what keeps a series that gained no data
 * handing back the same array.
 */
export class ClippedSeriesCache {
    private readonly bySeries = new WeakMap<object, ClippedEntry[]>();

    /**
     * Clips `series` to `[startMs, endMs]` on `timestampKey`.
     *
     * The entry is narrowed back to `TItem` because it was clipped from this very array under this very
     * `timestampKey`, so it is this caller's series.
     */
    clip<TItem extends Record<TTimestampKey, string>, TTimestampKey extends string>(
        series: TItem[],
        timestampKey: TTimestampKey,
        startMs: number | undefined,
        endMs: number | undefined
    ): TItem[] {
        const entries = this.bySeries.get(series) ?? [];
        const heldIndex = entries.findIndex(
            (entry) => entry.startMs === startMs && entry.endMs === endMs && entry.timestampKey === timestampKey
        );
        if (heldIndex !== -1) {
            const [held] = entries.splice(heldIndex, 1);
            // reading counts as a use, so a set of bounds still in rotation outlives one that is not
            entries.push(held);
            return held.clipped as TItem[];
        }

        const clipped = clipToRange(series, timestampKey, startMs, endMs);
        entries.push({ startMs, endMs, timestampKey, clipped });
        if (entries.length > CLIPPED_BOUNDS_PER_SERIES) {
            entries.shift();
        }
        this.bySeries.set(series, entries);

        return clipped;
    }
}

/** Shared, so every hook clipping the same series to the same bounds is handed one array. */
export const clippedSeriesCache = new ClippedSeriesCache();
